import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getQueryKeys } from "@/app/_module/services/helper";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";

import { leadsService } from "./services";

const namespace = "leads";

// Memoized agency context to prevent infinite re-renders
let cachedAgencyContext: { agencyId: string | null } | null = null;
let lastAgencyId: string | null = null;

// Helper function to get current agency context for query keys
const getAgencyContext = () => {
  const agencyId = AgencyTokenManager.getCurrentAgencyId();
  
  // Only create new context if agency ID has changed
  if (agencyId !== lastAgencyId) {
    lastAgencyId = agencyId;
    cachedAgencyContext = agencyId ? { agencyId } : { agencyId: null };
  }
  
  return cachedAgencyContext!;
};

// Query keys - combine helper keys with custom function keys
export const leadsQueryKeys = {
  // Standard keys from helper (these are strings, not functions)
  ...getQueryKeys(namespace),

  // Custom function keys for leads-specific functionality
  all: () => [namespace, getAgencyContext()] as const,
  lists: () => [namespace, "list", getAgencyContext()] as const,
  list: (filters: { page: number; limit: number; search?: string }) =>
    [namespace, "list", filters, getAgencyContext()] as const,
  details: () => [namespace, "detail", getAgencyContext()] as const,
  detail: (id: string) => [namespace, "detail", id, getAgencyContext()] as const,
  detailWithPolicies: (id: string) => [namespace, "detail", id, "policies", getAgencyContext()] as const,
  beneficiaries: (id: string) => [namespace, "beneficiaries", id, getAgencyContext()] as const,
  notes: (id: string) => [namespace, "notes", id, getAgencyContext()] as const,
  documents: (id: string) => [namespace, "documents", id, getAgencyContext()] as const,
  stats: () => [namespace, "stats", getAgencyContext()] as const,
  overview: () => [namespace, 'overview', getAgencyContext()] as const,
  statusesAvailable: () => [namespace, 'statuses', 'available', getAgencyContext()] as const,
  statusesSimple: () => [namespace, 'statuses', 'simple', getAgencyContext()] as const,
  deleted: (q?: string, page: number = 1, limit: number = 50) =>
    [namespace, "deleted", q || "", page, limit, getAgencyContext()] as const,
  byPhone: (phone: string) => [namespace, "byPhone", phone, getAgencyContext()] as const,

  // Lead list names (sources)
  listNames: () => [namespace, "list-names", getAgencyContext()] as const,

  // Pipeline stages
  pipelineStages: () => [namespace, "pipeline-stages", getAgencyContext()] as const,
  pipelineStagesAll: () => [namespace, "pipeline-stages", "all", getAgencyContext()] as const,
};

// Hook to get deleted leads. Optional `q` does fuzzy name/phone/email search.
export const useDeletedLeads = (q?: string, page: number = 1, limit: number = 50) => {
  return useQuery({
    queryKey: leadsQueryKeys.deleted(q, page, limit),
    queryFn: () => leadsService.getDeletedLeads(q, page, limit),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
